import { useQuery } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { mitschriftQuery, type BlattKennung, type MitschriftZeile } from '../../lib/mywork'
import type { StromTermin } from './strom'

/**
 * **Mitschreiben in der laufenden Terminkarte** ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md)
 * AP 24.23/24.24). Mockup „Termin läuft": links die Mitschrift mit Zeitmarken, darunter das Feld
 * mit der Leiste ☐ · ! · ? · @.
 *
 * Geschrieben wird ins **eine Blatt des Termins** im Notizbuch (`notizbuch.mywork.mitschreiben`) –
 * dasselbe Blatt, das „Heute" öffnet. Hier stehen nur die Zeilen, die während des Termins
 * entstanden sind (Zeitmarke ab 15 min vor Beginn); das ganze Blatt und das Protokoll ✦ gibt es
 * im Notizbuch („Großes Blatt ↗").
 */
export function Mitschrift({
  termin,
  blatt,
  feld,
  vorbereiten,
}: {
  termin: StromTermin
  blatt: BlattKennung | null | undefined
  /** Das eingebettete Festhalten (Ziel = dieser Termin). */
  feld: ReactNode
  vorbereiten?: (() => void) | undefined
}) {
  const { data: alle = [] } = useQuery(mitschriftQuery(termin.id, blatt))
  const [alleZeigen, setAlleZeigen] = useState(false)
  const zeilen = imTermin(alle, termin)
  const sichtbar = alleZeigen ? zeilen : zeilen.slice(-8)
  const adresse = blatt ? `/notizbuch#notiz-${blatt.notizbuchId}-${blatt.notizId}` : null

  return (
    <section
      aria-label={`Mitschrift ${termin.betreff}`}
      className="mt-2.5 flex flex-col gap-2 rounded-[12px] border border-[#d9eee3] bg-[#f6fbf8] px-3 py-2.5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-[10.5px] font-bold tracking-[0.08em] text-good-text uppercase">
          Mitschrift{zeilen.length ? ` · ${zeilen.length}` : ''}
        </h4>
        {adresse && (
          <a
            href={adresse}
            className="flex items-center gap-1 text-[12.5px] font-semibold text-accent-deep"
          >
            Großes Blatt <ExternalLink size={11} aria-hidden />
          </a>
        )}
      </div>
      {zeilen.length > sichtbar.length && (
        <button
          type="button"
          onClick={() => setAlleZeigen(true)}
          className="cursor-pointer self-start text-[12px] font-semibold text-accent-deep"
        >
          + {zeilen.length - sichtbar.length} frühere Zeilen
        </button>
      )}
      {sichtbar.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {sichtbar.map((z, i) => (
            <MitschriftZeileAnzeige key={`${z.marke}-${i}`} z={z} />
          ))}
        </ul>
      ) : (
        <p className="text-[12.5px] text-ink2">
          Noch nichts mitgeschrieben. Jede Zeile landet mit Uhrzeit im Blatt des Termins.
        </p>
      )}
      {feld}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink2">
        {vorbereiten && (
          <button
            type="button"
            onClick={vorbereiten}
            className="cursor-pointer font-semibold text-accent-deep"
          >
            Vorbereitung ansehen
          </button>
        )}
        <span>Danach: Protokoll ✦ im Blatt</span>
      </div>
    </section>
  )
}

/** Zeilen, die während des Termins geschrieben wurden – ab 15 min vor Beginn bis 1 h nach Ende. */
export function imTermin(
  zeilen: readonly MitschriftZeile[],
  termin: Pick<StromTermin, 'beginn' | 'ende'>,
): MitschriftZeile[] {
  const von = new Date(termin.beginn.slice(0, 19)).getTime() - 15 * 60_000
  const bis = new Date(termin.ende.slice(0, 19)).getTime() + 60 * 60_000
  return zeilen.filter((z) => {
    const t = Date.parse(z.marke)
    return t >= von && t <= bis
  })
}

/** Was eine Zeile ist – aus den Zeichen, die `Festhalten` setzt. */
export function zeilenArt(text: string): {
  art: 'aufgabe' | 'fremd' | 'beschluss' | 'frage' | 'notiz'
  text: string
} {
  const sauber = (t: string) =>
    t
      .replace(/\*\*/g, '')
      .replace(/\[\[|\]\]/g, '')
      .trim()
  if (/^☐\s/u.test(text)) return { art: 'aufgabe', text: sauber(text.slice(1)) }
  if (/^\[ \]\s/.test(text)) return { art: 'fremd', text: sauber(text.slice(3)) }
  if (/^\*\*Beschluss:\*\*/.test(text))
    return { art: 'beschluss', text: sauber(text.replace(/^\*\*Beschluss:\*\*/, '')) }
  if (/^\*\*Frage:\*\*/.test(text))
    return { art: 'frage', text: sauber(text.replace(/^\*\*Frage:\*\*/, '')) }
  return { art: 'notiz', text: sauber(text) }
}

const ZEICHEN: Record<ReturnType<typeof zeilenArt>['art'], string> = {
  aufgabe: '☐',
  fremd: '☐',
  beschluss: '!',
  frage: '?',
  notiz: '·',
}

function MitschriftZeileAnzeige({ z }: { z: MitschriftZeile }) {
  const { art, text } = zeilenArt(z.text)
  const uhr = new Date(z.marke).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  return (
    <li className={cn('flex gap-2 text-[13.5px] leading-snug', z.wartet && 'opacity-60')}>
      <span className="tabular w-[38px] shrink-0 pt-px text-[11.5px] text-muted">{uhr}</span>
      <span
        aria-hidden="true"
        className={cn(
          'w-3 shrink-0 text-center font-bold',
          art === 'aufgabe' && 'text-[#8a6100]',
          art === 'fremd' && 'text-muted',
          art === 'beschluss' && 'text-good-text',
          art === 'frage' && 'text-accent-deep',
          art === 'notiz' && 'text-muted',
        )}
      >
        {ZEICHEN[art]}
      </span>
      <span className={cn('min-w-0 flex-1', art === 'beschluss' && 'font-semibold')}>
        {art === 'beschluss' && <span className="sr-only">Beschluss: </span>}
        {art === 'frage' && <span className="sr-only">Frage: </span>}
        {text}
      </span>
    </li>
  )
}
